import {
  ApiError,
  Feature,
  FullPageLoader,
  LineString,
  LineStringFeatureProperties,
  Map,
  MapContent,
  MapContentRaw,
  hooks,
} from "@shared/primitive-components"
import { useEffect, useMemo } from "react"
import { useLDFlags } from "@shared/integration/launchdarkly"
import { MapFeatureTooltipContent, MapTitle } from "."
import { generateRouteFromGeoJson, getSafetyScoreSectionColor } from "../../../utils"
import { HERE_API_KEY } from "../../const"

type Props = {
  geojsonUrl: string | undefined
  setHasUnreliableTripData: (hasUnreliableTripData: boolean) => void
}

const generateTooltipContent = (geoJsonFeature: Feature<LineString, LineStringFeatureProperties>): JSX.Element => (
  <MapFeatureTooltipContent geoJsonFeature={geoJsonFeature} />
)

export const TripMap = ({ geojsonUrl, setHasUnreliableTripData }: Props) => {
  const { showHereMap } = useLDFlags()
  const [res] = hooks.useGetGeoJson(geojsonUrl || ``)

  const geojson = useMemo(() => res.result?.data, [res.result])
  const routeData = useMemo(() => (geojson ? generateRouteFromGeoJson(geojson) : null), [geojson])

  useEffect(() => {
    if (routeData && routeData.hasUnreliableSpeed) setHasUnreliableTripData(true)
  }, [routeData, setHasUnreliableTripData])

  if (res.result?.tag === `err`) return <ApiError error={res.result.error.type} />
  if (res.loading) return <FullPageLoader />
  if (!res.result || !geojson || !routeData) return <ApiError />

  const { route, start, end, bounds } = routeData
  const apiKey = HERE_API_KEY
  const height = showHereMap ? `45vh` : undefined

  return (
    <>
      {showHereMap && <MapTitle title="HERE Map as it will display in Production" />}
      <Map bounds={bounds} height={height}>
        <MapContent
          markers={
            start && end
              ? [
                  { position: start, content: `Start`.toUpperCase() },
                  { position: end, content: `End`.toUpperCase() },
                ]
              : []
          }
          styleFn={getSafetyScoreSectionColor}
          generateTooltipContentFn={generateTooltipContent}
          route={route}
          apiKey={apiKey}
        />
      </Map>
      {showHereMap && (
        <>
          <MapTitle title="HERE Map with data points for testing proposes" />
          <Map bounds={bounds} height={height}>
            <MapContentRaw
              styleFn={getSafetyScoreSectionColor}
              generateTooltipContentFn={generateTooltipContent}
              route={geojson}
              apiKey={apiKey}
            />
          </Map>
        </>
      )}
    </>
  )
}
